import React, { useState, useMemo, useEffect } from 'react';
import { CompleteKundali, Language, ChartStyle } from '../../types/kundali';
import {
  PastMilestoneCandidate,
  PastMilestoneCalibrationSummary,
  ObservedLifeEvent,
  CorroborationStatus,
  BTRState
} from '../../types/pastMilestoneCalibration';
import {
  calculatePastMilestoneCandidates,
  calculatePastMilestoneCalibrationSummary
} from '../../utils/v600/pastMilestoneCalibrationEngine';
import {
  History,
  CheckCircle2,
  AlertCircle,
  Clock,
  Briefcase,
  Home,
  Heart,
  BookOpen,
  Coins,
  Shield,
  Award,
  Sparkles,
  ChevronDown,
  ChevronUp,
  FileText,
  UserCheck,
  Compass,
  Layers,
  Scale,
  Calendar,
  Eye,
  Edit3,
  XCircle,
  HelpCircle,
  Info
} from 'lucide-react';

interface PastMilestoneCalibrationViewProps {
  kundali: CompleteKundali;
  lang?: Language;
  chartStyle?: ChartStyle;
  focusDate?: Date | string;
  onOpenConsultation?: (prompt?: string) => void;
}

const DOMAIN_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  career_karma: Briefcase,
  residence_griha: Home,
  relationship_marriage: Heart,
  education_vidya: BookOpen,
  wealth_finance: Coins,
  health_vitality: Shield,
  family_progeny: Award,
  spiritual_moksha: Compass,
  status_authority: Sparkles
};

export const PastMilestoneCalibrationView: React.FC<PastMilestoneCalibrationViewProps> = ({
  kundali,
  lang = 'en',
  chartStyle = 'north',
  focusDate,
  onOpenConsultation
}) => {
  const isHi = lang === 'hi';

  // Profile-specific storage key so past milestone calibrations persist across reloads and tab navigations
  const profileStorageKey = useMemo(() => {
    const b = kundali?.birthDetails;
    if (!b) return 'astro_past_milestones_default';
    const id = kundali.id || kundali.cloudId || `${b.name || 'unnamed'}_${b.date || ''}_${b.time || ''}`;
    return `astro_past_milestones_${id.replace(/[^a-zA-Z0-9_-]/g, '_')}`;
  }, [kundali?.id, kundali?.cloudId, kundali?.birthDetails]);

  // User-recorded events state (profile scoped in localStorage)
  const [userEvents, setUserEvents] = useState<Record<string, ObservedLifeEvent>>(() => {
    try {
      const saved = localStorage.getItem(profileStorageKey);
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (err) {
      console.error('Error restoring past milestone inputs:', err);
    }
    return {};
  });

  // Re-sync if active profile / kundali changes
  useEffect(() => {
    try {
      const saved = localStorage.getItem(profileStorageKey);
      if (saved) {
        setUserEvents(JSON.parse(saved));
        return;
      }
    } catch (err) {
      console.error('Error reloading past milestone inputs on profile change:', err);
    }
    setUserEvents({});
  }, [profileStorageKey]);

  // Automatically save to localStorage on every change
  useEffect(() => {
    try {
      if (Object.keys(userEvents).length > 0) {
        localStorage.setItem(profileStorageKey, JSON.stringify(userEvents));
      }
    } catch (err) {
      console.error('Error saving past milestone inputs:', err);
    }
  }, [userEvents, profileStorageKey]);

  const handleClearAllSaved = () => {
    try {
      localStorage.removeItem(profileStorageKey);
    } catch (err) {
      console.error('Error removing past milestone inputs:', err);
    }
    setUserEvents({});
  };

  const [activeEditingId, setActiveEditingId] = useState<string | null>(null);
  const [noteInput, setNoteInput] = useState<string>('');
  const [expandedAcharyaId, setExpandedAcharyaId] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'seeker' | 'acharya'>('seeker');
  const [btrState, setBtrState] = useState<BTRState>('WORKING');

  // Convert userEvents map to array for engine
  const observedEventsList = useMemo(() => {
    return Object.values(userEvents);
  }, [userEvents]);

  // Master Dynamic Milestone Calculation
  const candidates: PastMilestoneCandidate[] = useMemo(() => {
    return calculatePastMilestoneCandidates(kundali, {
      focusDate: focusDate || new Date(),
      btrState,
      observedEvents: observedEventsList
    });
  }, [kundali, focusDate, btrState, observedEventsList]);

  // Calibration Summary
  const summary: PastMilestoneCalibrationSummary = useMemo(() => {
    return calculatePastMilestoneCalibrationSummary(kundali, candidates);
  }, [kundali, candidates]);

  // Handle user response
  const handleUserConfirm = (candidate: PastMilestoneCandidate, confirmation: 'CONFIRMED_OCCURRED' | 'PARTIALLY_OCCURRED' | 'DID_NOT_OCCUR') => {
    const existing = userEvents[candidate.id];
    const updated: ObservedLifeEvent = {
      eventId: existing?.eventId || `obs_${candidate.id}`,
      eventTitle: candidate.themeEn,
      eventDescription: existing?.eventDescription || (confirmation === 'CONFIRMED_OCCURRED' ? 'Confirmed by native' : confirmation === 'PARTIALLY_OCCURRED' ? 'Partially occurred' : 'Did not occur as calculated'),
      approximateDateOrYear: candidate.startDate.split(' ')[2] || '2020',
      source: 'USER_INPUT',
      userConfirmation: confirmation,
      userNotes: existing?.userNotes || '',
      recordedAt: new Date().toISOString()
    };

    setUserEvents(prev => ({
      ...prev,
      [candidate.id]: updated
    }));
  };

  const handleSaveNote = (candidate: PastMilestoneCandidate) => {
    if (!noteInput.trim()) {
      setActiveEditingId(null);
      return;
    }

    const existing = userEvents[candidate.id];
    const updated: ObservedLifeEvent = {
      eventId: existing?.eventId || `obs_${candidate.id}`,
      eventTitle: candidate.themeEn,
      eventDescription: noteInput.trim(),
      approximateDateOrYear: candidate.startDate.split(' ')[2] || '2020',
      source: 'USER_INPUT',
      userConfirmation: existing?.userConfirmation || 'CONFIRMED_OCCURRED',
      userNotes: noteInput.trim(),
      recordedAt: new Date().toISOString()
    };

    setUserEvents(prev => ({
      ...prev,
      [candidate.id]: updated
    }));

    setNoteInput('');
    setActiveEditingId(null);
  };

  const getStatusBadge = (status: CorroborationStatus, badgeEn: string, badgeHi: string) => {
    switch (status) {
      case 'CORROBORATED':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800 border border-emerald-300 dark:bg-emerald-950/60 dark:text-emerald-300 dark:border-emerald-800">
            <CheckCircle2 className="w-3.5 h-3.5" />
            {isHi ? badgeHi : badgeEn}
          </span>
        );
      case 'PARTIALLY_CORROBORATED':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 border border-amber-300 dark:bg-amber-950/60 dark:text-amber-300 dark:border-amber-800">
            <AlertCircle className="w-3.5 h-3.5" />
            {isHi ? badgeHi : badgeEn}
          </span>
        );
      case 'NOT_CORROBORATED':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-rose-100 text-rose-800 border border-rose-300 dark:bg-rose-950/60 dark:text-rose-300 dark:border-rose-800">
            <XCircle className="w-3.5 h-3.5" />
            {isHi ? badgeHi : badgeEn}
          </span>
        );
      case 'USER_REPORTED':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-800 border border-blue-300 dark:bg-blue-950/60 dark:text-blue-300 dark:border-blue-800">
            <UserCheck className="w-3.5 h-3.5" />
            {isHi ? badgeHi : badgeEn}
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-indigo-50 text-indigo-800 border border-indigo-200 dark:bg-indigo-950/50 dark:text-indigo-300 dark:border-indigo-800">
            <Sparkles className="w-3.5 h-3.5" />
            {isHi ? badgeHi : badgeEn}
          </span>
        );
    }
  };

  return (
    <div className="space-y-6">
      {/* =================================================================== */}
      {/* 1. TOP PROVENANCE & SUMMARY BANNER                                 */}
      {/* =================================================================== */}
      <div className="bg-astro-card border border-astro-border rounded-2xl p-5 shadow-xs space-y-4">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-astro-border pb-4">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <span className="p-2 rounded-xl bg-amber-500/10 text-amber-600 border border-amber-500/20">
                <History className="w-5 h-5" />
              </span>
              <div>
                <h3 className="text-base font-bold text-astro-text font-serif">
                  {isHi ? 'अतीत जीवन घटनाएं एवं शास्त्रीय ज्योतिषीय सत्यापन' : 'Past Major Life Events & Astrological Calibration'}
                </h3>
                <p className="text-xs text-astro-secondary-text">
                  {isHi
                    ? 'जातक की जन्म कुंडली, विंशोत्तरी दशा व ऐतिहासिक गोचर से स्वतंत्र रूप से व्युत्पन्न'
                    : 'Independently derived from native canonical chart, historical Vimshottari Dashas & Gochar transits'}
                </p>
              </div>
            </div>
          </div>

          {/* Mode Selector */}
          <div className="flex items-center gap-2 self-start md:self-auto">
            <div className="bg-astro-surface border border-astro-border rounded-xl p-1 flex items-center gap-1 text-xs">
              <button
                onClick={() => setViewMode('seeker')}
                className={`px-3 py-1.5 rounded-lg font-medium transition-all ${
                  viewMode === 'seeker'
                    ? 'bg-astro-primary text-white shadow-xs'
                    : 'text-astro-secondary-text hover:text-astro-text'
                }`}
              >
                {isHi ? 'जिज्ञासु दृश्य (Seeker)' : 'Seeker View'}
              </button>
              <button
                onClick={() => setViewMode('acharya')}
                className={`px-3 py-1.5 rounded-lg font-medium transition-all ${
                  viewMode === 'acharya'
                    ? 'bg-amber-600 text-white shadow-xs'
                    : 'text-astro-secondary-text hover:text-astro-text'
                }`}
              >
                {isHi ? 'आचार्य प्रमाण (Acharya)' : 'Acharya Deep Dive'}
              </button>
            </div>
          </div>
        </div>

        {/* Provenance & BTR Meta Strip */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
          <div className="p-2.5 rounded-xl bg-astro-surface/70 border border-astro-border/70 space-y-0.5">
            <span className="text-[11px] text-astro-muted uppercase tracking-wider block">
              {isHi ? 'जातक एवं जन्म विवरण' : 'Native & Birth Profile'}
            </span>
            <span className="font-semibold text-astro-text truncate block">{summary.nativeName}</span>
            <span className="text-[10px] text-astro-secondary-text block">{summary.birthDate}</span>
          </div>

          <div className="p-2.5 rounded-xl bg-astro-surface/70 border border-astro-border/70 space-y-0.5">
            <span className="text-[11px] text-astro-muted uppercase tracking-wider block">
              {isHi ? 'सत्यापन स्थिति' : 'Calibration Progress'}
            </span>
            <span className="font-semibold text-emerald-700 dark:text-emerald-400 block">
              {summary.corroboratedCount} / {summary.totalCandidates} {isHi ? 'सत्यापित' : 'Corroborated'}
            </span>
            <span className="text-[10px] text-astro-secondary-text block">
              {isHi ? `औसत संगति: ${summary.averageAlignmentScore}%` : `Avg Alignment: ${summary.averageAlignmentScore}%`}
            </span>
          </div>

          <div className="p-2.5 rounded-xl bg-astro-surface/70 border border-astro-border/70 space-y-0.5">
            <span className="text-[11px] text-astro-muted uppercase tracking-wider block">
              {isHi ? 'जन्म समय परिशोधन (BTR)' : 'BTR Sensitivity'}
            </span>
            <span className="font-semibold text-astro-primary block">
              {summary.provenance.btrInterval}
            </span>
            <span className="text-[10px] text-astro-secondary-text truncate block">
              {isHi ? summary.btrOverallAssessmentHi : summary.btrOverallAssessmentEn}
            </span>
          </div>

          <div className="p-2.5 rounded-xl bg-astro-surface/70 border border-astro-border/70 space-y-0.5">
            <span className="text-[11px] text-astro-muted uppercase tracking-wider block">
              {isHi ? 'फोरेंसिक इंजन' : 'Forensic Provenance'}
            </span>
            <span className="font-semibold text-astro-text block">
              {summary.provenance.ayanamsha}
            </span>
            <span className="text-[10px] text-astro-secondary-text block">
              {summary.provenance.calculatedAt}
            </span>
          </div>
        </div>

        {/* Saved User Inputs Persistence Status Strip */}
        {Object.keys(userEvents).length > 0 && (
          <div className="flex items-center justify-between pt-2 border-t border-astro-border text-xs">
            <div className="flex items-center gap-1.5 text-emerald-700 dark:text-emerald-400 font-medium">
              <CheckCircle2 className="w-3.5 h-3.5 shrink-0" />
              <span>
                {isHi
                  ? `आपकी ${Object.keys(userEvents).length} प्रविष्टियां कुंडली में स्थायी रूप से सहेजी गई हैं।`
                  : `${Object.keys(userEvents).length} milestone feedback input(s) permanently saved to chart.`}
              </span>
            </div>
            <button
              onClick={handleClearAllSaved}
              className="text-[11px] text-astro-secondary-text hover:text-rose-600 transition-colors cursor-pointer underline decoration-dotted"
            >
              {isHi ? 'सहेजी गई प्रविष्टियां हटाएं' : 'Clear saved inputs'}
            </button>
          </div>
        )}
      </div>

      {/* =================================================================== */}
      {/* 2. DYNAMIC CANDIDATES LIST                                         */}
      {/* =================================================================== */}
      <div className="space-y-4">
        <div className="flex items-center justify-between px-1">
          <span className="text-xs font-bold uppercase tracking-wider text-astro-secondary-text flex items-center gap-1.5">
            <Calendar className="w-3.5 h-3.5 text-astro-accent" />
            {isHi ? 'कालक्रमबद्ध ऐतिहासिक मील के पत्थर' : 'Chronological Past Life Milestone Activations'}
          </span>
          <span className="text-[11px] text-astro-muted">
            {isHi ? 'जन्म विवरण आधारित गतिशील गणना' : 'Dynamically computed from natal Dasha & Gochar'}
          </span>
        </div>

        {candidates.length === 0 ? (
          <div className="p-8 text-center bg-astro-card border border-astro-border rounded-2xl space-y-2">
            <Info className="w-8 h-8 text-astro-muted mx-auto" />
            <p className="text-sm font-medium text-astro-text">
              {isHi ? 'इस जातक के लिए कोई ऐतिहासिक मील का पत्थर उपलब्ध नहीं है।' : 'No past historical milestones detected for this chart window.'}
            </p>
            <p className="text-xs text-astro-secondary-text">
              {isHi ? 'जातक की आयु अथवा जन्म तिथि की जांच करें।' : 'Verify native birth date and historical dasha coverage.'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4">
            {candidates.map((candidate, idx) => {
              const Icon = DOMAIN_ICONS[candidate.lifeDomain] || Sparkles;
              const isExpanded = expandedAcharyaId === candidate.id;
              const isEditing = activeEditingId === candidate.id;
              const observed = userEvents[candidate.id];

              return (
                <div
                  key={candidate.id}
                  className={`bg-astro-card border rounded-2xl p-5 space-y-4 transition-all ${
                    observed?.userConfirmation === 'CONFIRMED_OCCURRED'
                      ? 'border-emerald-500/40 bg-emerald-500/[0.02]'
                      : 'border-astro-border hover:border-astro-border-hover'
                  }`}
                >
                  {/* Card Top Header */}
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-astro-border/60 pb-3">
                    <div className="flex items-center gap-2.5">
                      <span className="p-2 rounded-xl bg-astro-secondary-surface text-astro-primary border border-astro-border">
                        <Icon className="w-4 h-4" />
                      </span>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-bold text-astro-primary font-mono">
                            {candidate.startDate} → {candidate.endDate}
                          </span>
                          <span className="text-[11px] text-astro-muted font-mono">
                            ({candidate.yearRange} • {isHi ? `आयु: ${candidate.ageAtEvent} वर्ष` : `Age: ${candidate.ageAtEvent} yrs`})
                          </span>
                        </div>
                        <h4 className="text-sm font-bold text-astro-text font-serif">
                          {isHi ? candidate.themeHi : candidate.themeEn}
                        </h4>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 self-start sm:self-auto">
                      {getStatusBadge(candidate.corroborationStatus, candidate.alignmentBadgeEn, candidate.alignmentBadgeHi)}
                    </div>
                  </div>

                  {/* Calculated Astrological Signature */}
                  <div className="p-3 rounded-xl bg-astro-secondary-surface/40 border border-astro-border/60 space-y-1.5">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-astro-muted block">
                      {isHi ? 'ज्योतिषीय योग एवं विंशोत्तरी दशा संकेत:' : 'Calculated Astrological Event Signature:'}
                    </span>
                    <p className="text-xs text-astro-text leading-relaxed">
                      {isHi ? candidate.eventSignatureHi : candidate.eventSignatureEn}
                    </p>
                    <div className="flex flex-wrap gap-2 text-[11px] text-astro-secondary-text pt-1">
                      <span className="inline-flex items-center gap-1 font-mono font-medium text-astro-primary">
                        <Clock className="w-3 h-3 text-astro-accent" />
                        Dasha: {candidate.dashaEvidence.mahadasha} → {candidate.dashaEvidence.antardasha}
                        {candidate.dashaEvidence.pratyantardasha && (
                          <span className="text-astro-accent font-semibold">
                            → {candidate.dashaEvidence.pratyantardasha} (PD)
                          </span>
                        )}
                      </span>
                      <span>•</span>
                      <span>Bhavas: [{candidate.bhavaEvidence.primaryBhavas.join(', ')}]</span>
                      <span>•</span>
                      <span>BTR: {isHi ? candidate.btrSensitivityNoteHi : candidate.btrSensitivityNoteEn}</span>
                    </div>

                    {/* Historical Panchanga Grounding at Milestone Peak */}
                    {candidate.historicalPanchangaSummary && (
                      <div className="flex flex-wrap items-center gap-1.5 pt-1.5 border-t border-astro-border/40 text-[11px]">
                        <span className="px-1.5 py-0.5 rounded-md bg-amber-500/10 text-amber-700 dark:text-amber-300 font-medium">
                          {isHi ? candidate.historicalPanchangaSummary.varaHi : candidate.historicalPanchangaSummary.vara}
                        </span>
                        <span className="px-1.5 py-0.5 rounded-md bg-astro-secondary-surface text-astro-secondary-text">
                          {isHi ? candidate.historicalPanchangaSummary.tithiHi : candidate.historicalPanchangaSummary.tithi} ({isHi ? candidate.historicalPanchangaSummary.pakshaHi : candidate.historicalPanchangaSummary.paksha})
                        </span>
                        <span className="px-1.5 py-0.5 rounded-md bg-astro-secondary-surface text-astro-secondary-text">
                          {isHi ? `नक्षत्र: ${candidate.historicalPanchangaSummary.nakshatraHi}` : `Moon: ${candidate.historicalPanchangaSummary.nakshatra}`}
                        </span>
                        {candidate.historicalPanchangaSummary.vratOrFestival && (
                          <span className="px-1.5 py-0.5 rounded-md bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 font-medium">
                            {isHi ? candidate.historicalPanchangaSummary.vratOrFestivalHi : candidate.historicalPanchangaSummary.vratOrFestival}
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Dynamic Forensic Verification Inquiry */}
                  <div className="p-3.5 rounded-xl bg-amber-500/5 border border-amber-500/20 space-y-2">
                    <div className="flex items-start gap-2">
                      <HelpCircle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                      <div className="space-y-1">
                        <span className="text-xs font-semibold text-amber-900 dark:text-amber-200 block">
                          {isHi ? 'सत्यापन प्रश्न (Forensic Corroboration Question):' : 'Forensic Calibration Question:'}
                        </span>
                        <p className="text-xs text-astro-text italic">
                          "{isHi ? candidate.verificationQuestionHi : candidate.verificationQuestionEn}"
                        </p>
                      </div>
                    </div>

                    {/* Interactive User Response Buttons */}
                    <div className="pt-2 border-t border-amber-500/15 flex flex-wrap items-center gap-2">
                      <button
                        onClick={() => handleUserConfirm(candidate, 'CONFIRMED_OCCURRED')}
                        className={`px-3 py-1 rounded-lg text-xs font-medium flex items-center gap-1.5 transition-all ${
                          observed?.userConfirmation === 'CONFIRMED_OCCURRED'
                            ? 'bg-emerald-600 text-white shadow-xs'
                            : 'bg-astro-surface hover:bg-emerald-50 text-astro-text border border-astro-border hover:border-emerald-300'
                        }`}
                      >
                        <CheckCircle2 className="w-3.5 h-3.5" />
                        <span>{isHi ? 'हाँ, यह घटना घटी थी' : 'Yes, occurred as indicated'}</span>
                      </button>

                      <button
                        onClick={() => handleUserConfirm(candidate, 'PARTIALLY_OCCURRED')}
                        className={`px-3 py-1 rounded-lg text-xs font-medium flex items-center gap-1.5 transition-all ${
                          observed?.userConfirmation === 'PARTIALLY_OCCURRED'
                            ? 'bg-amber-600 text-white shadow-xs'
                            : 'bg-astro-surface hover:bg-amber-50 text-astro-text border border-astro-border hover:border-amber-300'
                        }`}
                      >
                        <AlertCircle className="w-3.5 h-3.5" />
                        <span>{isHi ? 'आंशिक रूप से' : 'Partially / different scale'}</span>
                      </button>

                      <button
                        onClick={() => handleUserConfirm(candidate, 'DID_NOT_OCCUR')}
                        className={`px-3 py-1 rounded-lg text-xs font-medium flex items-center gap-1.5 transition-all ${
                          observed?.userConfirmation === 'DID_NOT_OCCUR'
                            ? 'bg-rose-600 text-white shadow-xs'
                            : 'bg-astro-surface hover:bg-rose-50 text-astro-text border border-astro-border hover:border-rose-300'
                        }`}
                      >
                        <XCircle className="w-3.5 h-3.5" />
                        <span>{isHi ? 'नहीं घटी' : 'Did not occur'}</span>
                      </button>

                      <button
                        onClick={() => {
                          setActiveEditingId(isEditing ? null : candidate.id);
                          setNoteInput(observed?.userNotes || '');
                        }}
                        className="px-3 py-1 rounded-lg text-xs font-medium bg-astro-surface hover:bg-astro-secondary-surface text-astro-primary border border-astro-border flex items-center gap-1.5 ml-auto"
                      >
                        <Edit3 className="w-3.5 h-3.5" />
                        <span>{observed?.userNotes ? (isHi ? 'टिप्पणी देखें/बदलें' : 'View/Edit Note') : (isHi ? 'टिप्पणी जोड़ें' : 'Add Event Details')}</span>
                      </button>
                    </div>

                    {/* Note Input Box */}
                    {isEditing && (
                      <div className="pt-3 space-y-2 animate-in fade-in duration-200">
                        <textarea
                          value={noteInput}
                          onChange={e => setNoteInput(e.target.value)}
                          placeholder={isHi ? 'इस काल में घटी वास्तविक घटना का विवरण दर्ज करें (उदा. नई नौकरी, विवाह, स्थानांतरण)...' : 'Record what actually occurred in your life during this window (e.g. Relocated to London, promoted to Lead)...'}
                          className="w-full text-xs p-2.5 rounded-xl bg-astro-surface border border-astro-border text-astro-text focus:outline-none focus:ring-1 focus:ring-astro-primary resize-none h-18"
                        />
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => setActiveEditingId(null)}
                            className="px-3 py-1 rounded-lg text-xs font-medium text-astro-secondary-text hover:text-astro-text"
                          >
                            {isHi ? 'रद्द करें' : 'Cancel'}
                          </button>
                          <button
                            onClick={() => handleSaveNote(candidate)}
                            className="px-3.5 py-1 rounded-lg text-xs font-medium bg-astro-primary text-white hover:bg-astro-accent shadow-xs"
                          >
                            {isHi ? 'सुरक्षित करें' : 'Save Event Note'}
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Displayed User Recorded Note */}
                    {observed?.userNotes && !isEditing && (
                      <div className="p-2.5 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-xs space-y-1">
                        <span className="font-semibold text-emerald-800 dark:text-emerald-300 flex items-center gap-1">
                          <UserCheck className="w-3.5 h-3.5" />
                          {isHi ? 'जातक द्वारा दर्ज वास्तविक घटना:' : 'Recorded Life Event:'}
                        </span>
                        <p className="text-astro-text italic">{observed.userNotes}</p>
                      </div>
                    )}
                  </div>

                  {/* Acharya Forensic Deep Dive Toggle */}
                  <div className="pt-1 border-t border-astro-border/50 flex items-center justify-between">
                    <button
                      onClick={() => setExpandedAcharyaId(isExpanded ? null : candidate.id)}
                      className="text-xs font-medium text-astro-primary hover:text-astro-accent flex items-center gap-1 cursor-pointer"
                    >
                      <Layers className="w-3.5 h-3.5" />
                      <span>{isExpanded ? (isHi ? 'आचार्य प्रमाण छुपाएं' : 'Hide Forensic Proof') : (isHi ? 'आचार्य प्रमाण व शास्त्रीय सूत्र देखें' : 'View Acharya Forensic Evidence')}</span>
                      {isExpanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                    </button>

                    {onOpenConsultation && (
                      <button
                        onClick={() => onOpenConsultation(`Please analyze the historical milestone calibration for ${candidate.themeEn} during ${candidate.yearRange}.`)}
                        className="text-xs font-medium text-astro-secondary-text hover:text-astro-primary flex items-center gap-1"
                      >
                        <Sparkles className="w-3.5 h-3.5 text-amber-500" />
                        <span>{isHi ? 'आचार्य से परामर्श करें' : 'Discuss with Acharya'}</span>
                      </button>
                    )}
                  </div>

                  {/* Acharya Forensic Drawer */}
                  {(isExpanded || viewMode === 'acharya') && (
                    <div className="bg-astro-secondary-surface/80 rounded-xl border border-astro-border p-4 space-y-3 text-xs animate-in fade-in duration-200">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div className="space-y-1.5">
                          <span className="font-bold text-astro-primary block uppercase tracking-wider text-[10px]">
                            {isHi ? 'विंशोत्तरी दशा व भावेश विश्लेषण:' : 'Vimshottari Dasha & Bhavesha:'}
                          </span>
                          <p className="text-astro-secondary-text leading-relaxed">
                            {isHi ? candidate.dashaEvidence.explanationHi : candidate.dashaEvidence.explanationEn}
                          </p>
                          <div className="space-y-0.5 text-[11px] text-astro-text">
                            {candidate.dashaEvidence.pratyantardasha && (
                              <div>• Pratyantardasha (PD): <span className="font-semibold text-astro-accent">{candidate.dashaEvidence.pratyantardasha}</span></div>
                            )}
                            <div>• Primary Bhavas: [{candidate.bhavaEvidence.primaryBhavas.join(', ')}]</div>
                            <div>• Active Lords: {candidate.bhaveshEvidence.activeBhaveshas.map(b => `${b.lord} (H${b.house})`).join(', ')}</div>
                            <div>• Natural Karaka: {candidate.karakaEvidence.naturalKarakas.join(', ')}</div>
                            {candidate.historicalPanchangaSummary && (
                              <div>• Panchanga: {candidate.historicalPanchangaSummary.vara}, {candidate.historicalPanchangaSummary.tithi} ({candidate.historicalPanchangaSummary.paksha})</div>
                            )}
                          </div>
                        </div>

                        <div className="space-y-1.5">
                          <span className="font-bold text-astro-primary block uppercase tracking-wider text-[10px]">
                            {isHi ? 'ऐतिहासिक गोचर व द्वि-गोचर प्रभाव:' : 'Historical Gochar & Double-Transit Resonance:'}
                          </span>
                          <p className="text-astro-secondary-text leading-relaxed">
                            {isHi ? candidate.gocharEvidence.explanationHi : candidate.gocharEvidence.explanationEn}
                          </p>
                          <div className="space-y-0.5 text-[11px] text-astro-text">
                            <div>• Transits: {candidate.gocharEvidence.keyTransits.join(', ')}</div>
                            {candidate.doubleTransitSummary && (
                              <div>• Double Transit (Guru/Shani): <span className="font-semibold text-astro-primary">Resonating Bhavas [{candidate.doubleTransitSummary.resonanceHouses.join(', ')}]</span></div>
                            )}
                            <div>• Sarvashtakavarga Bindus (H{candidate.acharyaDeepDive.ashtakavargaHouse}): <span className="font-bold">{candidate.acharyaDeepDive.ashtakavargaScore} bindus</span></div>
                            {candidate.vargaCorroboration.map((v, vIdx) => (
                              <div key={vIdx}>• {v.vargaName}: {isHi ? v.interpretationHi : v.interpretationEn}</div>
                            ))}
                          </div>
                        </div>
                      </div>

                      {/* Shloka Citation */}
                      <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20 space-y-1">
                        <span className="font-bold text-amber-900 dark:text-amber-200 block text-[11px]">
                          {candidate.acharyaDeepDive.classicalRuleSource}
                        </span>
                        <p className="font-serif italic text-astro-text text-xs">
                          "{isHi ? candidate.acharyaDeepDive.shlokaCitationHi : candidate.acharyaDeepDive.shlokaCitationEn}"
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
